import React from "react";
import PropTypes from "prop-types";

/**
 * DatePicker Component
 * A reusable date/datetime picker that supports both date and datetime-local input types.
 * Can be styled using Bootstrap Form.Control or custom inline styles.
 * 
 * @param {string} value - The current date value (in format expected by input type)
 * @param {function} onChange - Callback function when date changes: (value) => void
 * @param {string} type - Input type: 'date' or 'datetime-local' (default: 'date')
 * @param {string} label - Optional label text to display above the input
 * @param {string} placeholder - Optional placeholder text
 * @param {boolean} useBootstrap - Whether to use Bootstrap Form.Control styling (default: true)
 * @param {object} style - Optional inline styles for custom styling
 * @param {string} className - Optional additional CSS classes
 */
const DatePicker = ({
  value,
  onChange,
  type = "date",
  label = "",
  placeholder = "",
  useBootstrap = true,
  style = {},
  className = "",
}) => {
  // Handle change event and pass just the value to parent
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Default inline style for non-Bootstrap usage
  const defaultStyle = {
    padding: '8px',
    borderRadius: 6,
    border: '1px solid #CED4DA',
    width: '100%',
    fontSize: '14px',
    ...style,
  };

  // Determine which style and class to use
  const inputClassName = useBootstrap ? "form-control" : className;
  const inputStyle = useBootstrap ? {} : defaultStyle;

  return (
    <>
      {label && (
        <label
          style={{
            fontSize: useBootstrap ? '14px' : '12px',
            color: useBootstrap ? '#212529' : '#666',
            marginBottom: useBootstrap ? '0.25rem' : '4px',
            display: 'block',
            fontWeight: useBootstrap ? 400 : 500,
          }}
        >
          {label}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={inputClassName}
        style={inputStyle}
      />
    </>
  );
};

DatePicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['date', 'datetime-local']),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  useBootstrap: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
};

export default DatePicker;
